// How the jobs tab should react when applying a stage action fails, and what the action tooltip says.
//
// Extracted from ApplicationsTab so the branching is testable without mounting the tab: what the recruiter
// is told, whether another attempt is worth suggesting, and whether the offered actions are now stale are
// three separate decisions, and getting any of them wrong is what made a working push read as a failure.

// Refusals whose cause is that the offered list no longer matches the server: the action isn't offered at
// the candidate's current stage any more, the application or its job closed, the application moved to
// another job, its newest activity is on a profile the user can't see, or another action is mid-flight.
// Refetching is the fix; clicking the same item again can only fail the same way.
export const STALE_ACTION_CODES = [
  "action_unavailable", "action_pending", "closed", "no_application", "job_closed", "application_moved", "not_visible",
];

// The only failure another attempt can fix: the ATS never answered. Deliberately excludes ats_refused (the
// ATS evaluated the step and said no, so it will say no again) and ats_unauthorized (needs an admin).
export const RETRYABLE_CODES = ["ats_unavailable"];

const GENERIC = "Failed to update the stage.";
const GENERIC_RETRYABLE = "Failed to update the stage. Please try again.";

// The ATS's own wording runs long and is worth reading twice, so it earns a longer, dismissible toast; a
// generic message is short and self-explanatory, so it gets the default brief one. Both are finite — a
// duration: 0 toast clears only via its X, and here the X sits under the candidate sidebar drawer where it
// can't be relied on to dismiss, so a failed push could otherwise strand the toast on screen.
const SERVER_MESSAGE_TOAST_MS = 10000;
const GENERIC_TOAST_MS = 3000;

// The server refuses for eleven distinct reasons, and the ATS either refuses with a sentence of its own (a
// Workday Validation_Error names the step and the rule that restricted it), rejects our credentials, or
// never answers. "Please try again" is wrong for almost all of them, so the message and the retry advice
// are decided separately rather than shared.
//
// Retry advice is offered in two cases only: no code at all (a network failure or an unparseable response,
// where nothing classified the failure and "try again" is both conventional and usually right), and a code
// we know to be retryable. A code we don't recognize gets no such advice — the server classified the
// failure as something, and the asymmetry favours silence: suggesting a retry for a push that in fact
// landed is how a candidate gets moved through two steps.
export function describeApplyFailure(error) {
  // The real HTTP boundary (ApiError/handleApiError) hands us an ApiError carrying the parsed body on
  // `.data`; a raw axios rejection would carry it on `.response.data`. Read both so this works wherever it
  // is called from — reading only `.response.data` silently drops the server code in production.
  const data = error?.data || error?.response?.data || {};
  const code = data.code;
  const serverMessage = data.error;
  const retryable = !code || RETRYABLE_CODES.includes(code);

  // How the toast is shown, not just what it says: an ATS message gets the longer, dismissible treatment.
  // Decided here (unit-testable) rather than in the component, so a regression to a sticky (duration: 0) or
  // too-brief toast is caught without mounting the tab. See SERVER_MESSAGE_TOAST_MS.
  const hasServerMessage = Boolean(serverMessage);

  return {
    message: serverMessage || (retryable ? GENERIC_RETRYABLE : GENERIC),
    duration: hasServerMessage ? SERVER_MESSAGE_TOAST_MS : GENERIC_TOAST_MS,
    showClose: hasServerMessage,
    refetchActions: STALE_ACTION_CODES.includes(code),
    // The application moved to another job, so the row itself is on the wrong job.
    refetchApplications: code === "application_moved",
  };
}

// The preconditions and permitted security groups the ATS configuration carries for a step. The ATS checks
// both when the step is pushed and refuses it if either fails, so this is what a reviewer picks on when two
// actions share a destination and a label — and why an action can be offered and still be rejected. Absent
// for actions loaded before these were captured, and for non-Workday teams.
export function actionTooltip(action) {
  return [
    action?.conditionText && `Requires: ${action.conditionText}`,
    action?.securityGroups && `Allowed for: ${action.securityGroups}`,
  ].filter(Boolean).join("\n") || null;
}
